import { useAuth0 } from '@auth0/auth0-react'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Loader,
  Banner,
  Breadcrumbs,
  Button,
  Input,
  Label,
  LayerCard,
  Tabs,
  Text,
  useKumoToastManager,
} from '@cloudflare/kumo'
import { HouseIcon, BuildingsIcon } from '@phosphor-icons/react'
import { apiRequest, ApiError } from '../api/client'
import { useTranslation } from '../i18n'
import { PageHeader } from '../components/kumo/page-header/page-header'
import type {
  Building,
  BuildingHours,
  BuildingPhoto,
  BuildingAmenity,
  AmenityCategory,
} from '@hub-wifi/shared'

/* ------------------------------------------------------------------ */
/*  Default Hours                                                      */
/* ------------------------------------------------------------------ */

function defaultHours(): BuildingHours[] {
  return [0, 1, 2, 3, 4, 5, 6].map((dayOfWeek) => ({
    dayOfWeek,
    openTime: dayOfWeek >= 1 && dayOfWeek <= 5 ? '08:00' : '08:00',
    closeTime: dayOfWeek >= 1 && dayOfWeek <= 5 ? '18:00' : '18:00',
    isClosed: dayOfWeek === 0 || dayOfWeek === 6,
  }))
}

/* ------------------------------------------------------------------ */
/*  Preset Amenities                                                   */
/* ------------------------------------------------------------------ */

const PRESET_AMENITIES: BuildingAmenity[] = [
  { name: 'High-speed WiFi', icon: 'WifiHigh', category: 'connectivity' },
  { name: 'Fiber Internet', icon: 'Lightning', category: 'connectivity' },
  { name: 'Coffee & Tea', icon: 'Coffee', category: 'food' },
  { name: 'Kitchen', icon: 'CookingPot', category: 'food' },
  { name: 'Vending Machine', icon: 'Package', category: 'food' },
  { name: 'Parking', icon: 'Car', category: 'transport' },
  { name: 'Bike Rack', icon: 'Bicycle', category: 'transport' },
  { name: 'Near Metro', icon: 'Train', category: 'transport' },
  { name: 'Elevator', icon: 'Elevator', category: 'accessibility' },
  { name: 'Wheelchair Access', icon: 'Wheelchair', category: 'accessibility' },
  { name: 'Restrooms', icon: 'Toilet', category: 'facilities' },
  { name: 'Air Conditioning', icon: 'Thermometer', category: 'facilities' },
  { name: 'Printer', icon: 'Printer', category: 'facilities' },
  { name: 'Mail Service', icon: 'Envelope', category: 'facilities' },
  { name: 'Reception', icon: 'UserCircle', category: 'facilities' },
]

/* ------------------------------------------------------------------ */
/*  Form Data                                                          */
/* ------------------------------------------------------------------ */

interface BuildingFormData {
  name: string
  slug: string
  address: string
  city: string
  state: string
  zipCode: string
  country: string
  description: string
  latitude: string
  longitude: string
  googleMapsUrl: string
  timezone: string
  phone: string
  email: string
  imageUrl: string
  hours: BuildingHours[]
  photos: BuildingPhoto[]
  amenities: BuildingAmenity[]
}

const emptyForm: BuildingFormData = {
  name: '',
  slug: '',
  address: '',
  city: '',
  state: '',
  zipCode: '',
  country: '',
  description: '',
  latitude: '',
  longitude: '',
  googleMapsUrl: '',
  timezone: 'America/Sao_Paulo',
  phone: '',
  email: '',
  imageUrl: '',
  hours: defaultHours(),
  photos: [],
  amenities: [],
}

const DAY_KEYS = [
  'buildings.hours.sunday',
  'buildings.hours.monday',
  'buildings.hours.tuesday',
  'buildings.hours.wednesday',
  'buildings.hours.thursday',
  'buildings.hours.friday',
  'buildings.hours.saturday',
]

/* ------------------------------------------------------------------ */
/*  BuildingFormPage                                                    */
/* ------------------------------------------------------------------ */

export function BuildingFormPage() {
  const { id } = useParams<{ id: string }>()
  const isEdit = Boolean(id)
  const navigate = useNavigate()
  const { getAccessTokenSilently } = useAuth0()
  const { t } = useTranslation()
  const toasts = useKumoToastManager()

  const [form, setForm] = useState<BuildingFormData>(emptyForm)
  const [loading, setLoading] = useState(isEdit)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState('general')

  useEffect(() => {
    if (!id) return

    async function fetchBuilding() {
      try {
        const token = await getAccessTokenSilently()
        const building = await apiRequest<Building>(`/api/buildings/${id}`, { token })
        setForm({
          name: building.name,
          slug: building.slug,
          address: building.address,
          city: building.city,
          state: building.state,
          zipCode: building.zipCode,
          country: building.country,
          description: building.description,
          latitude: building.latitude?.toString() ?? '',
          longitude: building.longitude?.toString() ?? '',
          googleMapsUrl: building.googleMapsUrl ?? '',
          timezone: building.timezone,
          phone: building.phone ?? '',
          email: building.email ?? '',
          imageUrl: building.imageUrl ?? '',
          hours: building.hours.length > 0 ? building.hours : defaultHours(),
          photos: building.photos,
          amenities: building.amenities,
        })
      } catch (err) {
        setError(err instanceof ApiError ? err.message : t('common.error'))
      } finally {
        setLoading(false)
      }
    }

    void fetchBuilding()
  }, [id, getAccessTokenSilently, t])

  function setField<K extends keyof BuildingFormData>(key: K, value: BuildingFormData[K]) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  function updateHour(index: number, field: keyof BuildingHours, value: string | boolean | number) {
    setForm((prev) => {
      const hours = [...prev.hours]
      hours[index] = { ...hours[index], [field]: value } as BuildingHours
      return { ...prev, hours }
    })
  }

  function addPhoto() {
    setForm((prev) => ({
      ...prev,
      photos: [...prev.photos, { url: '', caption: '', sortOrder: prev.photos.length }],
    }))
  }

  function removePhoto(index: number) {
    setForm((prev) => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index),
    }))
  }

  function updatePhoto(index: number, field: keyof BuildingPhoto, value: string | number) {
    setForm((prev) => {
      const photos = [...prev.photos]
      photos[index] = { ...photos[index], [field]: value } as BuildingPhoto
      return { ...prev, photos }
    })
  }

  function toggleAmenity(amenity: BuildingAmenity) {
    setForm((prev) => {
      const exists = prev.amenities.some((a) => a.name === amenity.name)
      if (exists) {
        return { ...prev, amenities: prev.amenities.filter((a) => a.name !== amenity.name) }
      }
      return { ...prev, amenities: [...prev.amenities, amenity] }
    })
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSubmitting(true)

    try {
      const token = await getAccessTokenSilently()
      const payload = {
        ...form,
        latitude: form.latitude ? Number(form.latitude) : null,
        longitude: form.longitude ? Number(form.longitude) : null,
        googleMapsUrl: form.googleMapsUrl || null,
        phone: form.phone || null,
        email: form.email || null,
        imageUrl: form.imageUrl || null,
      }

      if (isEdit) {
        await apiRequest(`/api/buildings/${id}`, {
          method: 'PUT',
          token,
          body: payload,
        })
        toasts.add({ variant: 'success', title: t('buildings.updateSuccess') })
      } else {
        await apiRequest('/api/buildings', {
          method: 'POST',
          token,
          body: payload,
        })
        toasts.add({ variant: 'success', title: t('buildings.createSuccess') })
      }
      navigate('/buildings')
    } catch (err) {
      toasts.add({
        variant: 'error',
        title: t('common.errorTitle'),
        description: err instanceof ApiError ? err.message : t('common.error'),
      })
    } finally {
      setSubmitting(false)
    }
  }

  const amenitiesByCategory = PRESET_AMENITIES.reduce(
    (acc, amenity) => {
      if (!acc[amenity.category]) acc[amenity.category] = []
      acc[amenity.category].push(amenity)
      return acc
    },
    {} as Record<AmenityCategory, BuildingAmenity[]>,
  )

  return (
    <div>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs>
            <Breadcrumbs.Link icon={<HouseIcon size={14} />} href="/">
              {t('nav.home')}
            </Breadcrumbs.Link>
            <Breadcrumbs.Separator />
            <Breadcrumbs.Link icon={<BuildingsIcon size={14} />} href="/buildings">
              {t('buildings.title')}
            </Breadcrumbs.Link>
            <Breadcrumbs.Separator />
            <Breadcrumbs.Current>
              {isEdit ? t('buildings.editBuilding') : t('buildings.createBuilding')}
            </Breadcrumbs.Current>
          </Breadcrumbs>
        }
        title={isEdit ? t('buildings.editBuilding') : t('buildings.createBuilding')}
      />

      {loading && (
        <div className="flex items-center justify-center py-16">
          <Loader size="lg" />
        </div>
      )}

      {!loading && error && (
        <Banner variant="error">
          {t('common.errorPrefix')} {error}
        </Banner>
      )}

      {!loading && !error && (
        <LayerCard>
          <LayerCard.Primary>
            <form onSubmit={(e) => void handleSubmit(e)} className="flex flex-col gap-4">
              <Tabs
                tabs={[
                  { value: 'general', label: t('buildings.general') },
                  { value: 'hours', label: t('buildings.hours') },
                  { value: 'photos', label: t('buildings.photos') },
                  { value: 'amenities', label: t('buildings.amenities') },
                ]}
                value={activeTab}
                onValueChange={setActiveTab}
              />

              {activeTab === 'general' && (
                <div className="flex flex-col gap-3 pt-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex flex-col gap-1">
                      <Label>{t('buildings.name')}</Label>
                      <Input
                        value={form.name}
                        onChange={(e) => setField('name', e.target.value)}
                        required
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <Label>{t('buildings.slug')}</Label>
                      <Input
                        value={form.slug}
                        onChange={(e) =>
                          setField('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))
                        }
                        required
                      />
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <Label>{t('buildings.address')}</Label>
                    <Input
                      value={form.address}
                      onChange={(e) => setField('address', e.target.value)}
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="flex flex-col gap-1">
                      <Label>{t('buildings.city')}</Label>
                      <Input value={form.city} onChange={(e) => setField('city', e.target.value)} />
                    </div>
                    <div className="flex flex-col gap-1">
                      <Label>{t('buildings.state')}</Label>
                      <Input
                        value={form.state}
                        onChange={(e) => setField('state', e.target.value)}
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <Label>{t('buildings.zipCode')}</Label>
                      <Input
                        value={form.zipCode}
                        onChange={(e) => setField('zipCode', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex flex-col gap-1">
                      <Label>{t('buildings.country')}</Label>
                      <Input
                        value={form.country}
                        onChange={(e) => setField('country', e.target.value)}
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <Label>{t('buildings.timezone')}</Label>
                      <Input
                        value={form.timezone}
                        onChange={(e) => setField('timezone', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <Label>{t('buildings.description_field')}</Label>
                    <textarea
                      value={form.description}
                      onChange={(e) => setField('description', e.target.value)}
                      rows={3}
                      className="rounded-lg border border-kumo-ring bg-kumo-base px-3 py-2 text-sm"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex flex-col gap-1">
                      <Label>{t('buildings.phone')}</Label>
                      <Input
                        value={form.phone}
                        onChange={(e) => setField('phone', e.target.value)}
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <Label>{t('buildings.email')}</Label>
                      <Input
                        type="email"
                        value={form.email}
                        onChange={(e) => setField('email', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <Label>{t('buildings.imageUrl')}</Label>
                    <Input
                      value={form.imageUrl}
                      onChange={(e) => setField('imageUrl', e.target.value)}
                      placeholder="https://..."
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex flex-col gap-1">
                      <Label>{t('buildings.latitude')}</Label>
                      <Input
                        type="number"
                        step="any"
                        value={form.latitude}
                        onChange={(e) => setField('latitude', e.target.value)}
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <Label>{t('buildings.longitude')}</Label>
                      <Input
                        type="number"
                        step="any"
                        value={form.longitude}
                        onChange={(e) => setField('longitude', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <Label>{t('buildings.googleMapsUrl')}</Label>
                    <Input
                      value={form.googleMapsUrl}
                      onChange={(e) => setField('googleMapsUrl', e.target.value)}
                      placeholder="https://www.google.com/maps/..."
                    />
                  </div>
                </div>
              )}

              {activeTab === 'hours' && (
                <div className="flex flex-col gap-2 pt-3">
                  {form.hours.map((hour, index) => (
                    <div key={hour.dayOfWeek} className="flex items-center gap-3">
                      <span className="w-24 text-sm font-medium">
                        {t(DAY_KEYS[hour.dayOfWeek] ?? '')}
                      </span>
                      <label className="flex items-center gap-1.5">
                        <input
                          type="checkbox"
                          checked={hour.isClosed}
                          onChange={(e) => updateHour(index, 'isClosed', e.target.checked)}
                        />
                        <span className="text-xs text-kumo-subtle">
                          {t('buildings.hours.closed')}
                        </span>
                      </label>
                      {!hour.isClosed && (
                        <>
                          <Input
                            type="time"
                            value={hour.openTime}
                            onChange={(e) => updateHour(index, 'openTime', e.target.value)}
                            className="w-28"
                          />
                          <span className="text-sm text-kumo-subtle">-</span>
                          <Input
                            type="time"
                            value={hour.closeTime}
                            onChange={(e) => updateHour(index, 'closeTime', e.target.value)}
                            className="w-28"
                          />
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {activeTab === 'photos' && (
                <div className="flex flex-col gap-3 pt-3">
                  {form.photos.map((photo, index) => (
                    <div key={index} className="flex items-end gap-2">
                      <div className="flex-1 flex flex-col gap-1">
                        <Label>{t('buildings.photos.url')}</Label>
                        <Input
                          value={photo.url}
                          onChange={(e) => updatePhoto(index, 'url', e.target.value)}
                          placeholder="https://..."
                        />
                      </div>
                      <div className="flex-1 flex flex-col gap-1">
                        <Label>{t('buildings.photos.caption')}</Label>
                        <Input
                          value={photo.caption}
                          onChange={(e) => updatePhoto(index, 'caption', e.target.value)}
                        />
                      </div>
                      <Button
                        variant="secondary-destructive"
                        size="sm"
                        type="button"
                        onClick={() => removePhoto(index)}
                      >
                        {t('buildings.photos.remove')}
                      </Button>
                    </div>
                  ))}
                  <Button variant="secondary" size="sm" type="button" onClick={addPhoto}>
                    {t('buildings.photos.add')}
                  </Button>
                </div>
              )}

              {activeTab === 'amenities' && (
                <div className="flex flex-col gap-4 pt-3">
                  {(Object.keys(amenitiesByCategory) as AmenityCategory[]).map((category) => (
                    <div key={category}>
                      <Text
                        as="p"
                        variant="secondary"
                        size="sm"
                        DANGEROUS_className="font-medium mb-2"
                      >
                        {t(`buildings.amenities.${category}`)}
                      </Text>
                      <div className="flex flex-wrap gap-2">
                        {amenitiesByCategory[category].map((amenity) => {
                          const isSelected = form.amenities.some((a) => a.name === amenity.name)
                          return (
                            <button
                              key={amenity.name}
                              type="button"
                              onClick={() => toggleAmenity(amenity)}
                              className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                                isSelected
                                  ? 'bg-kumo-tint border-kumo-ring text-kumo-default'
                                  : 'bg-kumo-base border-kumo-line text-kumo-subtle hover:border-kumo-ring'
                              }`}
                            >
                              {amenity.name}
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="secondary" type="button" onClick={() => navigate('/buildings')}>
                  {t('buildings.cancel')}
                </Button>
                <Button variant="primary" type="submit" loading={submitting}>
                  {t('buildings.save')}
                </Button>
              </div>
            </form>
          </LayerCard.Primary>
        </LayerCard>
      )}
    </div>
  )
}
